import * as uuid from 'uuid';
import { ONE_DAY_MS, ONE_HOUR_MS, ONE_MINUTE_MS } from '../common/constants/time';
import { FlightOps } from '../common/flight/FlightOpsTypes';
import { getMinutesDiffFromDates, minutesToHumanHoursMinutes } from '../common/utils/time';
import { EventType } from '../scheduling/types';
import { computeAllDaysOffSums } from './calculationUtils/computeAllDaysOffSums';
import { DEFAULT_DUTY_TIMEZONE } from './ContactWithDutyTracking';
import { flightLegLikeHasLegId, isFlightLegLikeFromLogbookRecord, isSupervisingInstructorOnLegLike, } from './FinishedFlightLegLike';
import { DUTY_DAY_OFF, DUTY_ITEM_TYPE_ACCOMODATION_TRANSPORT, DUTY_ITEM_TYPE_BREAK, DUTY_ITEM_TYPE_FLIGHT, DUTY_ITEM_TYPE_FLIGHT_NC, DUTY_ITEM_TYPE_OTHER_DUTY, DUTY_REST, } from './types';
export { toQuarter } from './calculationUtils/computeAllDaysOffSums';
const WOCL_HOURS_LOCAL = [2, 6];
const FULL_NIGHT_HOURS_LOCAL = [24, 6];
const PREFLIGHT_TIME_MINUTES_HOMEBASE = 60;
const PREFLIGHT_TIME_MINUTES_AWAY = 45;
const POSTFLIGHT_TIME_MINUTES = 30;
export const computeDuties = (dutyItems, userHomebases, flightLegs, aircrafts, nextDuties, timezone = DEFAULT_DUTY_TIMEZONE) => {
    var _a;
    if (!dutyItems.length) {
        return [];
    }
    dutyItems.sort((a, b) => (b.startDate > a.startDate ? 1 : -1));
    if (nextDuties) {
        nextDuties.sort((a, b) => (b.dutyStartDate > a.dutyStartDate ? 1 : -1));
    }
    const output = [];
    const subLines = [];
    const finalizeDutyLine = (dutyLine) => {
        computeDutyTime(dutyLine);
        computeDutyFdp(dutyLine);
        computeDutyLineMaxFdp(dutyLine, timezone);
        output.push(dutyLine);
        addInterimSumsLines(output);
        dutyLine.subLines.forEach((subLine) => {
            subLines.push(subLine);
            addInterimSumsSubLines(subLines);
        });
    };
    let currentDutyLine = null;
    for (let i = 0; i < dutyItems.length; ++i) {
        const currentDutyItem = dutyItems[i];
        if (currentDutyLine === null) {
            currentDutyLine = {
                subLines: [],
                dutyStartDate: new Date(),
                dutyEndDate: new Date(currentDutyItem.endDate),
                dutyTimeMinutes: 0,
                dutyTimeCommercialActualMinutes: 0,
                dutyTimeCommercialMaxMinutes: 0,
                dutyTimeCommercialMaxPenalties: [],
                restActualBeforeMinutes: 0,
                restRequiredAfterMinutes: 0,
                dutySumCon7dMinutes: 0,
                dutySumCon28dMinutes: 0,
                dutySumCon12mMinutes: 0,
                sectorCount: 0,
                dutyPeriodSinceHomebase: 0,
                plannedFdpExtension: false,
                daysOff: null,
                dutyItems: [],
                meta: createEmptyTableLineMeta(),
            };
        }
        currentDutyLine.plannedFdpExtension || (currentDutyLine.plannedFdpExtension = currentDutyItem.plannedFdpExtension);
        currentDutyLine.dutyItems.push(currentDutyItem);
        currentDutyLine.dutyStartDate = new Date(currentDutyItem.startDate);
        const legs = ((_a = currentDutyItem.flightLegIds) === null || _a === void 0 ? void 0 : _a.length)
            ? currentDutyItem.flightLegIds
                .map((legId) => flightLegs.get(legId))
                .filter(Boolean)
            : null;
        const newSubLines = legs && legs.length > 0
            ? legs.map((l) => getSubLineFromLeg(l, currentDutyItem, aircrafts))
            : [getSubLineFromDutyItem(currentDutyItem)];
        newSubLines.sort((a, b) => b.date.getTime() - a.date.getTime());
        currentDutyLine.subLines.push(...newSubLines);
        currentDutyLine.sectorCount += legs ? getCommercialSectorCountFromLegs(legs) : currentDutyItem.sectorCount || 0;
        let nextDutyItem = dutyItems.at(i + 1);
        let nextDutyIsFromNextDuties = false;
        if (!nextDutyItem) {
            if (nextDuties && nextDuties.length > 0 && nextDuties[0].dutyItems.length) {
                [
                    {
                        dutyItems: [nextDutyItem],
                    },
                ] = nextDuties;
                nextDutyIsFromNextDuties = true;
            }
            else {
                currentDutyLine.restActualBeforeMinutes = undefined;
                finalizeDutyLine(currentDutyLine);
                break;
            }
        }
        const timeFromNext = currentDutyItem.startDate.getTime() - nextDutyItem.endDate.getTime();
        if (timeFromNext >= 10 * ONE_HOUR_MS || nextDutyItem.forceRestAfter) {
            currentDutyLine.restActualBeforeMinutes = getMinutesDiffFromDates(nextDutyItem.endDate, currentDutyItem.startDate);
            finalizeDutyLine(currentDutyLine);
            const restLine = computeRestSinceDuty(currentDutyItem.startDate, nextDutyItem, timezone);
            if (restLine.subLines[0].type === DUTY_DAY_OFF) {
                setLastDayOffs(restLine, subLines);
            }
            else if (nextDutyIsFromNextDuties) {
                const firstRestLine = nextDuties.find((l) => { var _a; return ((_a = l.subLines[0]) === null || _a === void 0 ? void 0 : _a.type) === DUTY_DAY_OFF; });
                if (firstRestLine) {
                    setLastDayOffs(firstRestLine, subLines);
                }
            }
            output.push(restLine);
            currentDutyLine = null;
        }
    }
    if (subLines.length > 0) {
        setLastDayOffs({ dutyEndDate: subLines.at(-1).date }, subLines);
    }
    computeAllDaysOffSums(output, subLines);
    computeDaysOffBase(output, nextDuties, userHomebases);
    computeRequiredRests(output, userHomebases);
    computeMetas(output, nextDuties, timezone);
    return output;
};
const computeRestSinceDuty = (date, nextDutyItem, timezone) => {
    const nextDate = nextDutyItem.endDate;
    const fullDaysOff = computeDaysOffBetweenDates(date, nextDate, timezone);
    if (fullDaysOff > 0) {
        const firstDayOff = new Date(toLocalDayMidday(nextDate, timezone).getTime() + ONE_DAY_MS);
        return {
            subLines: [
                {
                    date: firstDayOff,
                    dateEnd: new Date(firstDayOff.getTime() + (fullDaysOff - 1) * ONE_DAY_MS),
                    startDate: new Date(nextDate.getTime() + ONE_DAY_MS),
                    endDate: new Date(date.getTime() - ONE_DAY_MS),
                    type: DUTY_DAY_OFF,
                    ops: null,
                    registration: undefined,
                    blockTimeMinutes: 0,
                    startPlace: nextDutyItem.endPlaceIcao || undefined,
                    endPlace: nextDutyItem.endPlaceIcao || undefined,
                    blockSumCon28dMinutes: 0,
                    blockSumCon90dMinutes: 0,
                    blockSumCal1yMinutes: 0,
                    daysOffLastBeforeMinutes: 0,
                    daysOffCalendarMonthCount: 0,
                    daysOffCalendarQuarterCount: 0,
                    meta: emptyTableSubLineMeta,
                },
            ],
            dutyStartDate: new Date(nextDate),
            dutyEndDate: new Date(date),
            dutyTimeMinutes: getMinutesDiffFromDates(nextDate, date),
            dutyTimeCommercialActualMinutes: 0,
            dutyTimeCommercialMaxMinutes: 0,
            dutyTimeCommercialMaxPenalties: [],
            plannedFdpExtension: false,
            restActualBeforeMinutes: 0,
            restRequiredAfterMinutes: 0,
            dutySumCon7dMinutes: 0,
            dutySumCon28dMinutes: 0,
            dutySumCon12mMinutes: 0,
            sectorCount: 0,
            dutyPeriodSinceHomebase: 0,
            daysOff: fullDaysOff,
            dutyItems: [],
            meta: createEmptyTableLineMeta(),
        };
    }
    return {
        subLines: [
            {
                date: new Date(nextDate),
                dateEnd: new Date(date),
                startDate: new Date(nextDate),
                endDate: new Date(date),
                type: DUTY_REST,
                ops: null,
                registration: undefined,
                blockTimeMinutes: 0,
                startPlace: nextDutyItem.endPlaceIcao || undefined,
                endPlace: nextDutyItem.endPlaceIcao || undefined,
                blockSumCon28dMinutes: 0,
                blockSumCon90dMinutes: 0,
                blockSumCal1yMinutes: 0,
                daysOffLastBeforeMinutes: 0,
                daysOffCalendarMonthCount: 0,
                daysOffCalendarQuarterCount: 0,
                meta: emptyTableSubLineMeta,
            },
        ],
        dutyStartDate: new Date(nextDate),
        dutyEndDate: new Date(date),
        dutyTimeMinutes: getMinutesDiffFromDates(nextDate, date),
        dutyTimeCommercialActualMinutes: 0,
        dutyTimeCommercialMaxMinutes: 0,
        dutyTimeCommercialMaxPenalties: [],
        plannedFdpExtension: false,
        restActualBeforeMinutes: 0,
        restRequiredAfterMinutes: 0,
        dutySumCon7dMinutes: 0,
        dutySumCon28dMinutes: 0,
        dutySumCon12mMinutes: 0,
        sectorCount: 0,
        daysOff: 0,
        dutyPeriodSinceHomebase: 0,
        dutyItems: [],
        meta: createEmptyTableLineMeta(),
    };
};
export const computeDaysOffBetweenDates = (date, nextDate, timezone = DEFAULT_DUTY_TIMEZONE) => {
    const currentDutyHours = getLocalHours(date, timezone);
    const nextDutyHours = getLocalHours(nextDate, timezone);
    const currentDutyHadNight = currentDutyHours >= FULL_NIGHT_HOURS_LOCAL[1];
    const nextDutyHadNight = nextDutyHours <= FULL_NIGHT_HOURS_LOCAL[0];
    const daysDifference = getDaysBetweenDates(date, nextDate, timezone);
    let fullDaysOff = daysDifference - 1;
    if (!currentDutyHadNight) {
        fullDaysOff -= 1;
    }
    if (!nextDutyHadNight) {
        fullDaysOff -= 1;
    }
    return fullDaysOff;
};
const computeDutyTime = (dutyLine) => {
    dutyLine.dutyTimeMinutes = computeDutyTimeRaw(dutyLine.dutyStartDate, dutyLine.dutyEndDate, dutyLine.subLines);
};
export const computeDutyTimeRaw = (dutyStartDate, dutyEndDate, items) => {
    let dutyTimeMinutes = getMinutesDiffFromDates(dutyStartDate, dutyEndDate);
    for (const item of items) {
        if (item.type === DUTY_ITEM_TYPE_BREAK) {
            const length = item.endDate.getTime() - item.startDate.getTime();
            const lengthMinutes = length / ONE_MINUTE_MS;
            if (lengthMinutes >= 8 * 60) {
                dutyTimeMinutes -= Math.round(0.5 * lengthMinutes);
            }
        }
    }
    return dutyTimeMinutes;
};
const computeRequiredRests = (dutyLines, userHomebases) => {
    for (const dutyLine of dutyLines) {
        dutyLine.restRequiredAfterMinutes = computeRequiredRestRaw(dutyLine.dutyItems, userHomebases, dutyLine.subLines[0].endPlace, dutyLine.dutyPeriodSinceHomebase);
        dutyLine.travelRestAdjustment = getTravelRestAdjustment(dutyLine, userHomebases);
    }
};
const tooLongOffBaseStartTime = new Date(2025, 6, 14, 0, 0, 0).getTime();
const getIsLongTimeAway = (firstDutyItem, consecutivePeriodsSinceHomebase) => consecutivePeriodsSinceHomebase !== undefined &&
    consecutivePeriodsSinceHomebase >= 5 &&
    firstDutyItem.startDate.getTime() > tooLongOffBaseStartTime;
export const computeRequiredRestRaw = (dutyItems, userHomebases, endPlace, consecutivePeriodsSinceHomebase) => {
    if (dutyItems.length === 0) {
        return 0;
    }
    const dutyItemsFromOldest = [...dutyItems];
    dutyItemsFromOldest.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const firstDutyItem = dutyItemsFromOldest[0];
    const lastDutyItem = dutyItemsFromOldest.at(-1);
    let dutyMinutesWithoutLargeBreaks = getMinutesDiffFromDates(firstDutyItem.startDate, lastDutyItem.endDate);
    dutyItems.forEach((di) => {
        if (di.type === DUTY_ITEM_TYPE_BREAK) {
            const breakLengthMinutes = getMinutesDiffFromDates(di.startDate, di.endDate);
            if (breakLengthMinutes >= 8 * 60) {
                dutyMinutesWithoutLargeBreaks -= breakLengthMinutes;
            }
        }
    });
    endPlace || (endPlace = lastDutyItem.endPlaceIcao);
    const isHomeBase = placeIsHomebase(endPlace, userHomebases);
    const isLongTimeAway = getIsLongTimeAway(firstDutyItem, consecutivePeriodsSinceHomebase);
    const minRest = (isHomeBase || isLongTimeAway ? 12 : 10) * 60;
    const restWithoutExtensions = Math.max(minRest, dutyMinutesWithoutLargeBreaks);
    const hasPlannedFdpExtension = dutyItems.some((di) => di.plannedFdpExtension);
    if (hasPlannedFdpExtension) {
        return restWithoutExtensions + 4 * 60;
    }
    return restWithoutExtensions;
};
function placeIsHomebase(place, userHomebases) {
    return !place || place === '?' || userHomebases.includes(place);
}
const computeDutyFdp = (dutyLine) => {
    const inputs = dutyLine.dutyItems.map((di) => ({
        type: di.type,
        startDate: new Date(di.startDate),
        endDate: new Date(di.endDate),
    }));
    dutyLine.dutyTimeCommercialActualMinutes = getDutyFdp(inputs);
};
function getIsCommercialDuty(dutyItems) {
    return dutyItems.some((d) => d.type === DUTY_ITEM_TYPE_FLIGHT);
}
function getDutyStartsAtHomebase(thisStartPlace, previousEndPlace, userHomebases) {
    if (thisStartPlace) {
        return placeIsHomebase(thisStartPlace, userHomebases);
    }
    if (previousEndPlace) {
        return placeIsHomebase(previousEndPlace, userHomebases);
    }
    return true;
}
export const getDutyFdp = (dutyItems) => {
    dutyItems.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const isCommercialDuty = getIsCommercialDuty(dutyItems);
    if (!isCommercialDuty) {
        return 0;
    }
    const commercialDutyItems = stripTrailingNonCommercialDuties(dutyItems);
    const breakIndex = commercialDutyItems.findIndex((d) => d.type === DUTY_ITEM_TYPE_BREAK);
    if (breakIndex > 0 && commercialDutyItems.at(breakIndex + 1)) {
        const beforeBreak = getMinutesDiffFromDates(new Date(commercialDutyItems[0].startDate), new Date(commercialDutyItems[breakIndex - 1].endDate));
        const afterBreak = getMinutesDiffFromDates(new Date(commercialDutyItems[breakIndex + 1].startDate), new Date(commercialDutyItems.at(-1).endDate));
        return beforeBreak + afterBreak - POSTFLIGHT_TIME_MINUTES;
    }
    const totalDutyTime = getMinutesDiffFromDates(new Date(commercialDutyItems[0].startDate), new Date(commercialDutyItems.at(-1).endDate));
    return totalDutyTime - POSTFLIGHT_TIME_MINUTES;
};
const stripTrailingNonCommercialDuties = (dutyItems) => {
    let index = dutyItems.length - 1;
    while (index >= 0) {
        if (dutyItems[index].type === DUTY_ITEM_TYPE_FLIGHT) {
            break;
        }
        else {
            index--;
        }
    }
    return dutyItems.slice(0, index + 1);
};
const getSubLineFromLeg = (leg, baseItem, aircrafts) => {
    const aircraft = leg.aircraftId ? aircrafts.get(leg.aircraftId) : undefined;
    const date = new Date(leg.timeOut);
    const dateEnd = new Date(leg.timeIn);
    return {
        date,
        dateEnd,
        startDate: new Date(baseItem.startDate),
        endDate: new Date(baseItem.endDate),
        type: baseItem.type,
        ops: leg.ops,
        registration: (aircraft === null || aircraft === void 0 ? void 0 : aircraft.registration) || '-',
        blockTimeMinutes: getMinutesDiffFromDates(date, dateEnd),
        startPlace: leg.airportOffIcao,
        endPlace: leg.airportOnIcao,
        blockSumCon28dMinutes: 0,
        blockSumCon90dMinutes: 0,
        blockSumCal1yMinutes: 0,
        daysOffLastBeforeMinutes: Infinity,
        daysOffCalendarMonthCount: 0,
        daysOffCalendarQuarterCount: 0,
        meta: emptyTableSubLineMeta,
    };
};
const getSubLineFromDutyItem = (item) => {
    var _a, _b, _c;
    return ({
        date: new Date(item.startDate),
        dateEnd: new Date(item.endDate),
        startDate: new Date(item.startDate),
        endDate: new Date(item.endDate),
        type: item.type,
        ops: undefined,
        registration: '-',
        blockTimeMinutes: (_a = item.blockTimeMinutes) !== null && _a !== void 0 ? _a : undefined,
        startPlace: (_c = (_b = item.startPlaceIcao) !== null && _b !== void 0 ? _b : item.endPlaceIcao) !== null && _c !== void 0 ? _c : '-',
        endPlace: item.endPlaceIcao || '?',
        blockSumCon28dMinutes: 0,
        blockSumCon90dMinutes: 0,
        blockSumCal1yMinutes: 0,
        daysOffLastBeforeMinutes: Infinity,
        daysOffCalendarMonthCount: 0,
        daysOffCalendarQuarterCount: 0,
        meta: emptyTableSubLineMeta,
    });
};
const computeDutyLineMaxFdp = (dutyLine, timezone) => {
    const hasBreak = dutyLine.subLines.find((sl) => sl.type === DUTY_ITEM_TYPE_BREAK);
    const commercialDutyEndDate = getCommercialDutyEndDate(dutyLine);
    if (commercialDutyEndDate) {
        const { limit, penalties } = getDutyMaxFdp(!!hasBreak, dutyLine.plannedFdpExtension, dutyLine.sectorCount, dutyLine.dutyStartDate, commercialDutyEndDate, timezone);
        dutyLine.dutyTimeCommercialMaxMinutes = limit;
        dutyLine.dutyTimeCommercialMaxPenalties = penalties;
    }
};
export const getDutyMaxFdp = (dutyHasBreak, dutyHasPlannedFdpExtension, sectorCount, dutyStartDate, dutyCommercialEndDate, timezone) => {
    let limit = 13 * 60;
    const penalties = [];
    if (dutyHasBreak) {
        limit = 12 * 60;
        penalties.push('12 hours fixed for duty with break');
    }
    else {
        if (sectorCount >= 3) {
            const sectorReduction = 30 * (sectorCount - 2);
            const sectorReductionLimited = Math.min(120, sectorReduction);
            limit -= sectorReductionLimited;
            penalties.push(`-${sectorReductionLimited} minutes for ${sectorCount} sectors`);
        }
        const wocls = getWoclRangesForFlight(dutyStartDate, dutyCommercialEndDate, timezone);
        let woclPenaltyMinutes = 0;
        wocls.forEach((wocl) => {
            const { dutyStartedInWocl, minutesInWocl } = computeWoclData(wocl, dutyStartDate, dutyCommercialEndDate);
            if (dutyStartedInWocl) {
                woclPenaltyMinutes += Math.round(minutesInWocl);
            }
            else {
                woclPenaltyMinutes += Math.round(minutesInWocl / 2);
            }
        });
        if (woclPenaltyMinutes) {
            const woclPenaltyMinutesLimited = Math.min(120, woclPenaltyMinutes);
            limit -= woclPenaltyMinutesLimited;
            penalties.push(`-${woclPenaltyMinutesLimited} minutes for WOCL`);
        }
    }
    if (dutyHasPlannedFdpExtension) {
        const hoursStarted = Math.floor(getLocalHours(dutyStartDate, timezone));
        if (hoursStarted >= 22 || hoursStarted < 5) {
            limit = 11.75 * 60;
            penalties.push(`Fix 11:45 for planned extension starting between 22:00 and 04:59`);
        }
        else {
            limit += 60;
            penalties.push(`+60 minutes for planned extension`);
        }
    }
    return { limit, penalties };
};
const addInterimSumsLines = (lines) => {
    const lastDutyLine = lines.at(-1);
    const lastDutyTime = lastDutyLine.dutyTimeMinutes;
    const lastStartTime = lastDutyLine.dutyStartDate.getTime();
    const consecutive7DaysLimit = new Date(lastStartTime + 7 * ONE_DAY_MS);
    const consecutive28DaysLimit = new Date(lastStartTime + 28 * ONE_DAY_MS);
    const consecutive12MonthsLimit = new Date(lastStartTime);
    consecutive12MonthsLimit.setUTCFullYear(consecutive12MonthsLimit.getUTCFullYear() + 1);
    const currentOutputLength = lines.length;
    for (let i = currentOutputLength - 1; i >= 0; --i) {
        const precedingDutyLine = lines[i];
        if (precedingDutyLine.dutyEndDate < consecutive12MonthsLimit) {
            precedingDutyLine.dutySumCon12mMinutes += lastDutyTime;
            if (precedingDutyLine.dutyEndDate < consecutive28DaysLimit) {
                precedingDutyLine.dutySumCon28dMinutes += lastDutyTime;
                if (precedingDutyLine.dutyEndDate < consecutive7DaysLimit) {
                    precedingDutyLine.dutySumCon7dMinutes += lastDutyTime;
                }
            }
        }
    }
};
const addInterimSumsSubLines = (subLines) => {
    const lastSubLine = subLines.at(-1);
    const lastBlockTime = lastSubLine.blockTimeMinutes;
    if (lastBlockTime === undefined || lastBlockTime === 0) {
        return;
    }
    const lastStartTime = lastSubLine.startDate.getTime();
    const consecutive28DaysLimit = new Date(lastStartTime + 28 * ONE_DAY_MS);
    const consecutive90DaysLimit = new Date(lastStartTime + 90 * ONE_DAY_MS);
    const thisYear = lastSubLine.startDate.getUTCFullYear();
    const currentSublinesLength = subLines.length;
    for (let i = currentSublinesLength - 1; i >= 0; --i) {
        const precedingSubLine = subLines[i];
        if (precedingSubLine.dateEnd < consecutive90DaysLimit) {
            precedingSubLine.blockSumCon90dMinutes += lastBlockTime;
            if (precedingSubLine.dateEnd < consecutive28DaysLimit) {
                precedingSubLine.blockSumCon28dMinutes += lastBlockTime;
            }
        }
        if (precedingSubLine.dateEnd.getUTCFullYear() === thisYear) {
            precedingSubLine.blockSumCal1yMinutes += lastBlockTime;
        }
    }
    const consecutive12MonthsLimit = new Date(lastStartTime);
    consecutive12MonthsLimit.setUTCFullYear(consecutive12MonthsLimit.getUTCFullYear() + 1);
};
const setLastDayOffs = (dayOffLine, subLines) => {
    const currentSublinesLength = subLines.length;
    for (let i = currentSublinesLength - 1; i >= 0; --i) {
        const precedingSubLine = subLines[i];
        const sublineDate = precedingSubLine.date;
        if (!Number.isFinite(precedingSubLine.daysOffLastBeforeMinutes)) {
            const timeDifference = sublineDate.getTime() - dayOffLine.dutyEndDate.getTime();
            precedingSubLine.daysOffLastBeforeMinutes = Math.floor(timeDifference / ONE_MINUTE_MS);
        }
    }
};
const computeMetas = (dutyLines, nextDuties, timezone) => {
    dutyLines.forEach((line, lineIndex) => {
        var _a;
        const previousLine = dutyLines[lineIndex + 1]
            ? dutyLines[lineIndex + 1]
            : nextDuties
                ? nextDuties[0]
                : undefined;
        const previousDutyLine = previousLine &&
            (previousLine.subLines[0].type === DUTY_REST || previousLine.subLines[0].type === DUTY_DAY_OFF)
            ?
                ((_a = dutyLines[lineIndex + 2]) !== null && _a !== void 0 ? _a : nextDuties === null || nextDuties === void 0 ? void 0 : nextDuties[0])
            : previousLine;
        line.meta = Object.assign(Object.assign({}, line.meta), { previousLine, previousDutyLine });
    });
    computeFdpExtensionRestChanges(dutyLines);
    const extensionStarts = [...new Set([...(nextDuties !== null && nextDuties !== void 0 ? nextDuties : []), ...dutyLines])]
        .filter((line) => line.plannedFdpExtension && line.dutyItems.length)
        .map((line) => line.dutyStartDate);
    dutyLines.forEach((line) => {
        const sublineCount = line.subLines.length;
        const { previousDutyLine } = line.meta;
        const breakSubLineIndex = line.subLines.findIndex((sl) => sl.type === DUTY_ITEM_TYPE_BREAK);
        line.meta = Object.assign(Object.assign({}, line.meta), { sublineCount, breakSubLineIndex });
        const isCommercialDuty = getIsCommercialDuty(line.dutyItems);
        const restRequiredBeforeMinutes = previousDutyLine && getRestRequiredAfterMinutes(previousDutyLine);
        const previousRestWasReduced = Boolean(previousDutyLine && getRestReductionMinutes(previousDutyLine) > 0);
        const restReductionMinutes = getRestReductionMinutes(line);
        if (restReductionMinutes > 0) {
            line.meta.restNotices.add(`Reduced rest before: rest required after extended by ${minutesToHumanHoursMinutes(restReductionMinutes)}`);
        }
        const travelAdjustmentMinutes = getTravelRestAdjustmentMinutes(line);
        if (travelAdjustmentMinutes !== 0) {
            line.meta.restNotices.add(travelAdjustmentMinutes < 0
                ? `Short travel to accommodation: rest required reduced by ${minutesToHumanHoursMinutes(-travelAdjustmentMinutes)}`
                : `Long travel to accommodation: rest required extended by ${minutesToHumanHoursMinutes(travelAdjustmentMinutes)}`);
        }
        line.meta.plannedFdpExtensionErrors = getPlannedFdpExtensionErrors(line, extensionStarts, timezone);
        line.subLines.forEach((subLine, subLineIndex) => {
            const isLastDuty = subLineIndex === 0;
            const isFirstDuty = subLineIndex === sublineCount - 1;
            const isRest = subLine.type === DUTY_REST;
            const isTimeOff = subLine.type === DUTY_DAY_OFF || isRest;
            const isBreak = subLine.type === DUTY_ITEM_TYPE_BREAK;
            const lineDurationMinutes = (subLine.endDate.getTime() - subLine.startDate.getTime()) / ONE_MINUTE_MS;
            const isTooShortBreak = isBreak && lineDurationMinutes < 180;
            const previousSubline = line.subLines[subLineIndex + 1];
            const previousDutyIsDifferentType = previousSubline && previousSubline.type !== subLine.type;
            const nextSubline = line.subLines[subLineIndex - 1];
            const nextDutyIsDifferentType = nextSubline && nextSubline.type !== subLine.type;
            const isCommercialDutyEnd = subLine.type === DUTY_ITEM_TYPE_FLIGHT && (isLastDuty || nextSubline.type !== DUTY_ITEM_TYPE_FLIGHT);
            const fdpOverLimitMinutes = line.dutyTimeCommercialActualMinutes - line.dutyTimeCommercialMaxMinutes;
            const previousWasBreak = (previousSubline === null || previousSubline === void 0 ? void 0 : previousSubline.type) === DUTY_ITEM_TYPE_BREAK;
            const restDeficitMinutes = restRequiredBeforeMinutes && line.restActualBeforeMinutes
                ? restRequiredBeforeMinutes - line.restActualBeforeMinutes
                : 0;
            const restPeriodIsTooShort = Boolean(line.restActualBeforeMinutes && line.restActualBeforeMinutes < 10 * 60);
            const restWasLessThanRequired = restDeficitMinutes > 0;
            if (restDeficitMinutes > 0) {
                line.meta.restErrors.add(`Rest required was ${minutesToHumanHoursMinutes(restRequiredBeforeMinutes)}`);
            }
            const fdpOverLimitDanger = fdpOverLimitMinutes > 120;
            const fdpOverLimitWarning = fdpOverLimitMinutes > 0;
            const restBeforeWarning = isCommercialDuty && Boolean(restWasLessThanRequired);
            const restBeforeDanger = isCommercialDuty &&
                Boolean(restPeriodIsTooShort ||
                    restDeficitMinutes >= 120 ||
                    (restWasLessThanRequired && previousRestWasReduced));
            const block28dWarning = isCommercialDuty && subLine.blockSumCon28dMinutes > 100 * 60 * 0.9;
            const block28dDanger = isCommercialDuty && subLine.blockSumCon28dMinutes > 100 * 60;
            const block90dWarning = isCommercialDuty && subLine.blockSumCon90dMinutes > 280 * 60 * 0.9;
            const block90dDanger = isCommercialDuty && subLine.blockSumCon90dMinutes > 280 * 60;
            const block1yWarning = isCommercialDuty && subLine.blockSumCal1yMinutes > 900 * 60 * 0.9;
            const block1yDanger = isCommercialDuty && subLine.blockSumCal1yMinutes > 900 * 60;
            const daysOffLastBeforeDanger = isCommercialDuty && subLine.daysOffLastBeforeMinutes > 168 * 60;
            const daysOffCalendarMonthWarning = isCommercialDuty && subLine.daysOffCalendarMonthCount <= 8;
            const daysOffCalendarMonthDanger = isCommercialDuty && subLine.daysOffCalendarMonthCount < 7;
            const daysOffCalendarQuarterWarning = isCommercialDuty && subLine.daysOffCalendarQuarterCount <= 27;
            const daysOffCalendarQuarterDanger = isCommercialDuty && subLine.daysOffCalendarQuarterCount < 24;
            let commercialDutyTimeReductionDueNCSuffix = 0;
            if (isCommercialDutyEnd && !isLastDuty) {
                const commercialTimeMinutes = line.dutyTimeCommercialActualMinutes;
                commercialDutyTimeReductionDueNCSuffix = line.dutyTimeMinutes - commercialTimeMinutes;
            }
            const duty7dWarning = isCommercialDutyEnd &&
                line.dutySumCon7dMinutes - commercialDutyTimeReductionDueNCSuffix > 60 * 60 * 0.9;
            const duty7dDanger = isCommercialDutyEnd && line.dutySumCon7dMinutes - commercialDutyTimeReductionDueNCSuffix > 60 * 60;
            const duty28dWarning = isCommercialDutyEnd &&
                line.dutySumCon28dMinutes - commercialDutyTimeReductionDueNCSuffix > 190 * 60 * 0.9;
            const duty28dDanger = isCommercialDutyEnd && line.dutySumCon28dMinutes - commercialDutyTimeReductionDueNCSuffix > 190 * 60;
            const duty12mWarning = isCommercialDutyEnd &&
                line.dutySumCon12mMinutes - commercialDutyTimeReductionDueNCSuffix > 2000 * 60 * 0.9;
            const duty12mDanger = isCommercialDutyEnd && line.dutySumCon12mMinutes - commercialDutyTimeReductionDueNCSuffix > 2000 * 60;
            subLine.meta = {
                isLastDuty,
                isFirstDuty,
                isRest,
                isTimeOff,
                isBreak,
                lineDurationMinutes,
                isTooShortBreak,
                previousSubline,
                previousDutyIsDifferentType,
                nextSubline,
                nextDutyIsDifferentType,
                fdpOverLimitMinutes,
                previousWasBreak,
                restPeriodIsShort: restPeriodIsTooShort,
                restWasLessThanRequired,
                fdpOverLimitDanger,
                fdpOverLimitWarning,
                restBeforeWarning,
                restBeforeDanger,
                duty7dWarning,
                duty7dDanger,
                duty28dWarning,
                duty28dDanger,
                duty12mWarning,
                duty12mDanger,
                block28dWarning,
                block28dDanger,
                block90dWarning,
                block90dDanger,
                block1yWarning,
                block1yDanger,
                daysOffLastBeforeDanger,
                daysOffCalendarMonthWarning,
                daysOffCalendarMonthDanger,
                daysOffCalendarQuarterWarning,
                daysOffCalendarQuarterDanger,
                isCommercialDutyEnd,
                commercialDutyTimeReductionDueNCSuffix,
            };
        });
    });
};
const computeDaysOffBase = (dutyLines, nextDuties, userHomebases) => {
    const linesFromOldest = [...dutyLines];
    linesFromOldest.reverse();
    linesFromOldest.forEach((line, lineIndex) => {
        var _a, _b;
        const previousLine = linesFromOldest[lineIndex - 1]
            ? linesFromOldest[lineIndex - 1]
            : nextDuties
                ? nextDuties[0]
                : undefined;
        if (line.subLines[0].type === DUTY_REST && !line.daysOff && previousLine) {
            line.dutyPeriodSinceHomebase = previousLine.dutyPeriodSinceHomebase;
        }
        else {
            const startsAtHomebase = getDutyStartsAtHomebase((_a = line.subLines.at(-1)) === null || _a === void 0 ? void 0 : _a.startPlace, (_b = previousLine === null || previousLine === void 0 ? void 0 : previousLine.subLines[0]) === null || _b === void 0 ? void 0 : _b.endPlace, userHomebases);
            if (startsAtHomebase) {
                line.dutyPeriodSinceHomebase = 0;
            }
            else if (previousLine) {
                if (previousLine.daysOff) {
                    line.dutyPeriodSinceHomebase = 1;
                }
                else if (previousLine) {
                    line.dutyPeriodSinceHomebase = previousLine.dutyPeriodSinceHomebase + 1;
                    if (previousLine.daysOff && previousLine.dutyPeriodSinceHomebase > 0) {
                        line.dutyPeriodSinceHomebase += previousLine.daysOff - 1;
                    }
                }
            }
            else {
                line.dutyPeriodSinceHomebase = 1;
            }
        }
    });
};
function computeFdpExtensionRestChanges(dutyLines) {
    dutyLines.forEach((line) => {
        const { previousDutyLine } = line.meta;
        if (line.plannedFdpExtension &&
            (previousDutyLine === null || previousDutyLine === void 0 ? void 0 : previousDutyLine.restRequiredAfterMinutes) &&
            line.restActualBeforeMinutes &&
            line.restRequiredAfterMinutes) {
            const surplusMinutes = line.restActualBeforeMinutes - getRestRequiredAfterMinutes(previousDutyLine);
            if (surplusMinutes > 120) {
                previousDutyLine.restRequiredAfterMinutes += 120;
                line.restRequiredAfterMinutes -= 120;
                previousDutyLine.meta.restNotices.add('Duty extension: added 2 hours of rest required after due to extension on next duty');
                line.meta.restNotices.add('Duty extension: Setting only 2 hours of extra rest after required due to sufficient rest before');
            }
        }
    });
}
const MAX_REDUCING_TRAVEL_MINUTES = 90;
const TRAVEL_REST_BASE_MINUTES = 120;
function getTravelRestAdjustment(line, userHomebases) {
    const items = [...line.dutyItems].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    const lastItem = items.at(-1);
    const travel = lastItem === null || lastItem === void 0 ? void 0 : lastItem.travelToAccommodationMinutes;
    if (!lastItem || travel === null || travel === undefined || travel < 0) {
        return undefined;
    }
    if (travel > TRAVEL_REST_BASE_MINUTES) {
        return { minutes: travel - TRAVEL_REST_BASE_MINUTES, minRestMinutes: 0 };
    }
    if (travel > MAX_REDUCING_TRAVEL_MINUTES ||
        placeIsHomebase(line.subLines[0].endPlace || lastItem.endPlaceIcao, userHomebases)) {
        return undefined;
    }
    return {
        minutes: travel - TRAVEL_REST_BASE_MINUTES,
        minRestMinutes: (getIsLongTimeAway(items[0], line.dutyPeriodSinceHomebase) ? 12 : 10) * 60,
    };
}
const applyTravelRestAdjustment = (line, restMinutes) => {
    const adjustment = line.travelRestAdjustment;
    if (!adjustment) {
        return restMinutes;
    }
    return adjustment.minutes >= 0
        ? restMinutes + adjustment.minutes
        : Math.min(restMinutes, Math.max(adjustment.minRestMinutes, restMinutes + adjustment.minutes));
};
function getRestReductionMinutes(line) {
    const { previousDutyLine } = line.meta;
    const restRequiredBeforeMinutes = (previousDutyLine === null || previousDutyLine === void 0 ? void 0 : previousDutyLine.restRequiredAfterMinutes) &&
        applyTravelRestAdjustment(previousDutyLine, previousDutyLine.restRequiredAfterMinutes);
    if (!restRequiredBeforeMinutes || !line.restActualBeforeMinutes || !getIsCommercialDuty(line.dutyItems)) {
        return 0;
    }
    return Math.max(0, restRequiredBeforeMinutes - line.restActualBeforeMinutes);
}
export function getRestRequiredAfterMinutes(line) {
    return line.restRequiredAfterMinutes === undefined
        ? undefined
        : applyTravelRestAdjustment(line, line.restRequiredAfterMinutes + getRestReductionMinutes(line));
}
export function getTravelRestAdjustmentMinutes(line) {
    if (line.restRequiredAfterMinutes === undefined) {
        return 0;
    }
    const beforeTravel = line.restRequiredAfterMinutes + getRestReductionMinutes(line);
    return applyTravelRestAdjustment(line, beforeTravel) - beforeTravel;
}
const MAX_FDP_EXTENSIONS_IN_7_DAYS = 2;
function getPlannedFdpExtensionErrors(line, extensionStarts, timezone) {
    const plannedFdpExtensionErrors = new Set();
    if (line.plannedFdpExtension) {
        const sectorCountSum = line.dutyItems.reduce((sum, di) => sum + di.sectorCount, 0);
        const wocls = getWoclRangesForFlight(line.dutyStartDate, line.dutyEndDate, timezone);
        const woclEncroachment = wocls.reduce((sum, wocl) => {
            const { minutesInWocl } = computeWoclData(wocl, line.dutyStartDate, line.dutyEndDate);
            return sum + minutesInWocl;
        }, 0);
        if (woclEncroachment >= 120 && sectorCountSum > 2) {
            plannedFdpExtensionErrors.add(`With WOCL encroachment of more than 2 hours, FDP cannot be extended on flight with more than 2 sectors`);
        }
        else if (woclEncroachment > 0 && sectorCountSum > 4) {
            plannedFdpExtensionErrors.add(`With WOCL encroachment, FDP cannot be extended on flight with more than 4 sectors`);
        }
        else if (sectorCountSum > 5) {
            plannedFdpExtensionErrors.add(`FDP extension is not allowed on flight with 6 or more sectors.`);
        }
        const earlierInSevenDays = extensionStarts.filter((start) => start.getTime() < line.dutyStartDate.getTime() &&
            getDaysBetweenDates(line.dutyStartDate, start, timezone) <= 6);
        if (earlierInSevenDays.length >= MAX_FDP_EXTENSIONS_IN_7_DAYS) {
            plannedFdpExtensionErrors.add('Maximum number of extensions in 7 days is two');
        }
    }
    return plannedFdpExtensionErrors;
}
function createEmptyTableLineMeta() {
    return {
        sublineCount: 0,
        previousLine: undefined,
        previousDutyLine: undefined,
        breakSubLineIndex: 0,
        plannedFdpExtensionErrors: new Set(),
        restErrors: new Set(),
        restNotices: new Set(),
    };
}
export const emptyTableSubLineMeta = {
    isLastDuty: false,
    isFirstDuty: false,
    isRest: false,
    isTimeOff: false,
    isBreak: false,
    lineDurationMinutes: 0,
    isTooShortBreak: false,
    previousSubline: undefined,
    previousDutyIsDifferentType: false,
    nextSubline: undefined,
    nextDutyIsDifferentType: false,
    fdpOverLimitMinutes: 0,
    previousWasBreak: false,
    restPeriodIsShort: false,
    restWasLessThanRequired: false,
    fdpOverLimitDanger: false,
    fdpOverLimitWarning: false,
    restBeforeWarning: false,
    restBeforeDanger: false,
    duty7dWarning: false,
    duty7dDanger: false,
    duty28dWarning: false,
    duty28dDanger: false,
    duty12mWarning: false,
    duty12mDanger: false,
    block28dWarning: false,
    block28dDanger: false,
    block90dWarning: false,
    block90dDanger: false,
    block1yWarning: false,
    block1yDanger: false,
    daysOffLastBeforeDanger: false,
    daysOffCalendarMonthWarning: false,
    daysOffCalendarMonthDanger: false,
    daysOffCalendarQuarterWarning: false,
    daysOffCalendarQuarterDanger: false,
    isCommercialDutyEnd: false,
    commercialDutyTimeReductionDueNCSuffix: 0,
};
export const flightLegCommercialOps = [
    FlightOps.CAT,
    FlightOps.ATO_NCC,
    FlightOps.ATO_NCO,
    FlightOps.SPO,
];
export const getDutyItemFromLegs = (baseDutyItem, legsInput, homebases) => {
    var _a, _b;
    const legs = [...legsInput].sort((a, b) => a.timeOut.getTime() - b.timeOut.getTime());
    const anySectorCount = legs.length;
    const legsActuallyFlown = legs.filter((leg) => !isSupervisingInstructorOnLegLike(leg, baseDutyItem.crewId));
    if (legsActuallyFlown.length === 0 && anySectorCount > 0) {
        const [startDate, endDate] = getDutyTimesFromLegs(legs, homebases);
        const type = DUTY_ITEM_TYPE_OTHER_DUTY;
        const startPlaceIcao = legs[0].airportOffIcao;
        const flightLegIds = [];
        const logbookRecordIds = legs
            .filter((leg) => isSupervisingInstructorOnLegLike(leg, baseDutyItem.crewId))
            .map((l) => l.id);
        return Object.assign(Object.assign({}, baseDutyItem), { id: uuid.v4(), startDate,
            endDate, sectorCount: 0, type, blockTimeMinutes: 0, startPlaceIcao, endPlaceIcao: startPlaceIcao, flightLegIds,
            logbookRecordIds, note: 'Supervising Solo' });
    }
    else if (((_a = legs[0]) === null || _a === void 0 ? void 0 : _a.reservationEventType) !== undefined) {
        return Object.assign(Object.assign({}, baseDutyItem), { startDate: new Date(legs[0].timeOut), endDate: new Date(legs[0].timeIn), sectorCount: 0, blockTimeMinutes: 0, startPlaceIcao: legs[0].airportOffIcao, endPlaceIcao: legs[0].airportOnIcao, flightLegIds: [legs[0].id], type: getDutyTypeFromReservationType((_b = legs[0]) === null || _b === void 0 ? void 0 : _b.reservationEventType) });
    }
    else if (anySectorCount) {
        const [startDate, endDate] = getDutyTimesFromLegs(legs, homebases);
        const type = legs.some((l) => flightLegCommercialOps.includes(l.ops))
            ? DUTY_ITEM_TYPE_FLIGHT
            : DUTY_ITEM_TYPE_FLIGHT_NC;
        const flightLegIds = legsActuallyFlown.filter(flightLegLikeHasLegId).map((l) => l.legId);
        const logbookRecordIds = legsActuallyFlown
            .filter(isFlightLegLikeFromLogbookRecord)
            .map((l) => l.logbookRecordId);
        const blockTimeMinutes = legsActuallyFlown.reduce((previousValue, leg) => {
            const timeDiff = Math.max(0, leg.timeIn.getTime() - leg.timeOut.getTime());
            return previousValue + Math.floor(timeDiff / ONE_MINUTE_MS);
        }, 0);
        const lastLeg = legs.at(-1);
        const startPlaceIcao = legs[0].airportOffIcao;
        const endPlaceIcao = lastLeg.airportOnIcao;
        return Object.assign(Object.assign({}, baseDutyItem), { id: uuid.v4(), startDate,
            endDate, sectorCount: getCommercialSectorCountFromLegs(legs), type,
            flightLegIds,
            logbookRecordIds,
            blockTimeMinutes,
            startPlaceIcao,
            endPlaceIcao, source: legs[0].source });
    }
    const startDate = new Date(baseDutyItem.startDate);
    const endDate = new Date(baseDutyItem.endDate);
    return Object.assign(Object.assign({}, baseDutyItem), { id: uuid.v4(), startDate,
        endDate, sectorCount: getCommercialSectorCountFromLegs(legs) });
};
export const getDutyTimesFromLegs = (legs, homebases) => {
    var _a;
    const legsFromNewest = [...legs].sort((a, b) => b.timeOut.getTime() - a.timeOut.getTime());
    const firstLeg = legsFromNewest.at(-1);
    const lastLeg = legsFromNewest[0];
    const startSlipMinutes = homebases.includes(firstLeg.airportOffIcao)
        ? PREFLIGHT_TIME_MINUTES_HOMEBASE
        : PREFLIGHT_TIME_MINUTES_AWAY;
    const endSlipMinutes = POSTFLIGHT_TIME_MINUTES;
    const startBasis = (_a = firstLeg.plannedTimeOut) !== null && _a !== void 0 ? _a : firstLeg.timeOut;
    const startDate = new Date(startBasis.getTime() - startSlipMinutes * ONE_MINUTE_MS);
    const endTime = lastLeg.timeIn.getTime();
    const endDate = new Date(endTime + endSlipMinutes * ONE_MINUTE_MS);
    return [startDate, endDate];
};
export const getWoclRangesForFlight = (dutyStartDate, dutyEndDate, timezone) => {
    const hoursOffsetStart = getOffsetHoursForTimezone(dutyStartDate, timezone);
    const hoursOffsetEnd = getOffsetHoursForTimezone(dutyEndDate, timezone);
    const woclStart = new Date(dutyStartDate);
    woclStart.setUTCHours(WOCL_HOURS_LOCAL[0] - hoursOffsetStart, 0, 0, 0);
    const woclEnd = new Date(dutyStartDate);
    woclEnd.setUTCHours(WOCL_HOURS_LOCAL[1] - hoursOffsetEnd, 0, 0, 0);
    const wocls = [[woclStart, woclEnd]];
    const startLocalDay = dutyStartDate.toLocaleDateString('en-US', { timeZone: timezone });
    const endLocalDay = dutyEndDate.toLocaleDateString('en-US', { timeZone: timezone });
    if (startLocalDay !== endLocalDay) {
        const wocl2Start = new Date(dutyEndDate);
        wocl2Start.setUTCHours(WOCL_HOURS_LOCAL[0] - hoursOffsetEnd, 0, 0, 0);
        const wocl2End = new Date(dutyEndDate);
        wocl2End.setUTCHours(WOCL_HOURS_LOCAL[1] - hoursOffsetEnd, 0, 0, 0);
        wocls.push([wocl2Start, wocl2End]);
    }
    return wocls;
};
function getOffsetHoursForTimezone(date, timezone) {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const timeDifference = tzDate.getTime() - utcDate.getTime();
    return timeDifference / ONE_HOUR_MS;
}
function getLocalHours(date, timezone) {
    const offset = getOffsetHoursForTimezone(date, timezone);
    return (date.getUTCHours() + date.getUTCMinutes() / 60 + offset + 24) % 24;
}
export const computeWoclData = ([woclStart, woclEnd], dutyStartDate, dutyCommercialEndDate) => {
    const dutyEndedBeforeWocl = dutyCommercialEndDate < woclStart;
    const dutyStartedAfterWocl = dutyStartDate > woclEnd;
    if (dutyEndedBeforeWocl || dutyStartedAfterWocl) {
        return { dutyStartedInWocl: false, minutesInWocl: 0 };
    }
    const dutyStartedInWocl = dutyStartDate > woclStart;
    const lowerOfWoclAndDutyEnd = Math.min(dutyCommercialEndDate.getTime(), woclEnd.getTime());
    const timeInWocl = dutyStartedInWocl
        ? lowerOfWoclAndDutyEnd - dutyStartDate.getTime()
        : lowerOfWoclAndDutyEnd - woclStart.getTime();
    const minutesInWocl = timeInWocl / ONE_MINUTE_MS;
    return { minutesInWocl, dutyStartedInWocl };
};
export const getDaysBetweenDates = (date1, date2, timezone) => {
    const date1Midday = toLocalDayMidday(date1, timezone);
    const date2Midday = toLocalDayMidday(date2, timezone);
    return Math.round((date1Midday.getTime() - date2Midday.getTime()) / ONE_DAY_MS);
};
function toLocalDayMidday(date, timezone) {
    const localDate = new Date(date.getTime() + getOffsetHoursForTimezone(date, timezone) * ONE_HOUR_MS);
    return new Date(Date.UTC(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate(), 12));
}
function getCommercialSectorCountFromLegs(legs) {
    const fromOldest = [...legs].sort((a, b) => a.timeOut.getTime() - b.timeOut.getTime());
    let lastCommercialIndex = -1;
    for (const [i, element] of fromOldest.entries()) {
        if (flightLegCommercialOps.includes(element.ops)) {
            lastCommercialIndex = i;
        }
    }
    if (lastCommercialIndex === -1) {
        return 0;
    }
    return lastCommercialIndex + 1;
}
function getCommercialDutyEndDate(dutyLine) {
    let lastCommercial = null;
    for (const subLine of dutyLine.subLines) {
        if (subLine.type === DUTY_ITEM_TYPE_FLIGHT &&
            (!lastCommercial || subLine.dateEnd.getTime() > lastCommercial.getTime())) {
            lastCommercial = new Date(subLine.dateEnd);
        }
    }
    return lastCommercial;
}
function getDutyTypeFromReservationType(reservationEvent) {
    if (reservationEvent === EventType.travel) {
        return DUTY_ITEM_TYPE_ACCOMODATION_TRANSPORT;
    }
    return DUTY_ITEM_TYPE_OTHER_DUTY;
}
//# sourceMappingURL=DutyCalculator.js.map